import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import ActionsMenuButton from 'src/components-new/primitives/ActionsMenuButton'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, ExportRow} from './exportsColumns'
import ExportDialogNew, {ExportRequestValues} from './ExportDialogNew'

// Same setInterval cadence as the legacy page (src/pages/export/Export.tsx), used to pick up
// "in elaborazione" -> "creato"/"errore" status transitions without a manual reload.
const POLL_INTERVAL_MS = 60000
// Same delay as the legacy page's post-submit refresh: long enough for the backend to have
// persisted the "in elaborazione" row, which it does before starting the (slow) generation.
const REFRESH_AFTER_SUBMIT_MS = 1000
const READY_TOAST_DURATION_MS = 8000

// Same value/label pairs as the legacy page's EXPORT_TYPES + headerActions (src/pages/export/Export.tsx)
// — kept here (duplicated, not imported) so both stacks show identical Italian labels and the
// same set of report types without pages-new reaching into src/pages/**. Which of these are
// actually offered is still PSP-driven, via `exportConfigurations.exportTypes`.
const EXPORT_TYPE_OPTIONS: {value: string; label: string}[] = [
  {value: 'tutor-doctor-report', label: 'Report dashboard'},
  {value: 'tutor-doctor-report-pesi', label: 'Report peso paziente'},
  {value: 'tutor-customer-dashboard', label: 'Report mensile attività - Dashboard'},
  {value: 'tutor-customer-adherences', label: 'Report file aderenze'},
  {value: 'tutor-customer-report-pesi', label: 'Report peso paziente'},
  {value: 'tutor-customer-questionnaire', label: 'Report questionari di soddisfazione'},
  {value: 'tutor-customer-app-portali', label: 'Report uso app e portali'},
  {value: 'quiperte-customer-dashboard', label: 'Genera report dashboard'},
  {value: 'quiperte-customer-questionnaire', label: 'Genera report questionari'},
  {value: 'quiperte-customer-app-portali', label: 'Genera report uso APP/Portali'},
]

/**
 * New-style equivalent of src/pages/export/Export.tsx. No page title is rendered here —
 * HeaderBar derives it from the "export" nav item label. The export type is chosen from the
 * "Esporta" dropdown menu (mirrors the legacy page's headerActions, each of which sets
 * `exportId` and only then opens ExportDialog — the dialog itself never offers a type picker).
 */
export default function ExportsListNew(): JSX.Element {
  const exportConfigurations = useAppSelector(state => state.pspConfigReducer.data.exportConfigurations)
  const toast = useToast()

  const [rows, setRows] = useState<ExportRow[]>([])
  const [count, setCount] = useState(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-createdAt'})
  const [dialogExportType, setDialogExportType] = useState<string>()
  const [downloadingId, setDownloadingId] = useState<string>()

  const fetchData = useCallback((opts?: {silent?: boolean}) => {
    const silent = opts?.silent ?? false
    const noop = () => {}
    const paginationFilters = {rowsPerPage: pagination.rowsPerPage, page: pagination.page, order: pagination.order}

    Promise.all([
      apiClient.getExports(activeFilters, silent ? noop : setStatus, paginationFilters),
      apiClient.getExportCount(activeFilters, silent ? noop : setStatusCount),
    ]).then(([data, countResult]) => {
      if (Array.isArray(data)) setRows(data)
      const n = Number(countResult)
      if (!isNaN(n)) setCount(n)
    })
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // postExport only resolves once the file is generated, which can take minutes: the request
  // is left running in the background (see handleSaveExport), so its completion may land after
  // this page has unmounted. These refs let it refresh the table only while still mounted and
  // with the current filters/pagination instead of the ones captured at submit time.
  const mountedRef = useRef(true)
  const fetchDataRef = useRef(fetchData)
  fetchDataRef.current = fetchData
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Silent background refresh: `silent: true` swaps in no-op status setters so a poll tick
  // never flashes the table back to its loading spinner, unlike the initial/filtered fetch.
  useEffect(() => {
    const interval = setInterval(() => fetchData({silent: true}), POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  // Fire-and-forget: the dialog closes right away so the user can keep navigating the portal.
  // The global toast (ToastProvider lives in ThemeProviderNew, above the router) notifies them
  // when the file is ready, wherever they are; the file itself is then found in this table.
  const handleSaveExport = (values: ExportRequestValues) => {
    const label = enabledExportTypes.find(opt => opt.value === values.exportType)?.label ?? 'Export'
    let finalStatus: string | undefined

    setDialogExportType(undefined)
    toast.info('File in elaborazione')

    const refreshTimer = setTimeout(() => {
      if (mountedRef.current) fetchDataRef.current({silent: true})
    }, REFRESH_AFTER_SUBMIT_MS)

    apiClient.postExport(values, (s: string) => { finalStatus = s })
      .catch(() => { finalStatus = STATUS.REJECTED })
      .then(() => {
        clearTimeout(refreshTimer)
        if (finalStatus === STATUS.FULFILLED) {
          toast.success(`Il file "${label}" è pronto`, {duration: READY_TOAST_DURATION_MS})
        } else {
          toast.error(`Generazione di "${label}" non riuscita. Verifica lo stato nella pagina delle esportazioni.`, {duration: READY_TOAST_DURATION_MS})
        }
        if (mountedRef.current) fetchDataRef.current({silent: true})
      })
  }

  const handleDownload = async (row: ExportRow) => {
    if (!row.fileServiceName) {
      toast.error('File non disponibile per il download.')
      return
    }
    setDownloadingId(row._id)
    let finalStatus: string | undefined
    const data = await apiClient.downloadExport(row.fileServiceName, (s: string) => { finalStatus = s })
    setDownloadingId(undefined)

    if (finalStatus === STATUS.REJECTED || !data) {
      toast.error('Download non riuscito.')
      return
    }

    const blob = new Blob([data], {type: 'application/octet-stream'})
    const elem = document.createElement('a')
    elem.href = window.URL.createObjectURL(blob)
    elem.download = row.filename ?? 'export'
    document.body.appendChild(elem)
    elem.click()
    document.body.removeChild(elem)
    window.URL.revokeObjectURL(elem.href)
  }

  const filterConfig: FilterItem[] = exportConfigurations?.filterConfig ?? []
  const enabledExportTypes = EXPORT_TYPE_OPTIONS.filter(
    opt => exportConfigurations?.exportTypes?.includes(opt.value)
  )
  const selectedExportType = enabledExportTypes.find(opt => opt.value === dialogExportType)
  const columns = buildColumns({
    columnConfig: exportConfigurations?.columnOptions,
    onDownload: handleDownload,
    downloadingId,
  })

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 2}}>
        {enabledExportTypes.length > 0 && (
          <ActionsMenuButton
            items={enabledExportTypes}
            label="Genera report"
            onSelect={setDialogExportType}
          />
        )}
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          severity="error"
          sx={{mb: 2}}
        >
          {'Errore nel recuperare le esportazioni.'}
        </InlineMessage>
      )}

      <DataTable<ExportRow>
        columns={columns}
        emptyMessage="Nessuna esportazione trovata"
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        onPaginationChange={setPagination}
        pagination={pagination}
        rows={rows}
        totalElements={count}
      />

      {selectedExportType && (
        <ExportDialogNew
          exportType={selectedExportType.value}
          exportTypeLabel={selectedExportType.label}
          onClose={() => setDialogExportType(undefined)}
          onSave={handleSaveExport}
          open
        />
      )}
    </Box>
  )
}
